import { AlertCircleIcon } from "lucide-react-native";
import React, { memo } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
} from "react-hook-form";
import { Platform, type TextInputProps } from "react-native";

import { IForm } from "@/features/notes/type";

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "@/components/atoms/FormControl";
import { Box } from "@/components/atoms/Box";

/**
 * iOS-only `lineHeight: 0` for the multiline note TextInput. iOS adds
 * extra vertical lead to multiline inputs; zeroing the line height keeps
 * the text vertically centered, matching the former iOS zero-leading
 * class. lineHeight 0 has no closed text-style token (the design-system
 * variants bundle non-zero leadings), so it lives here as a hoisted style
 * constant. `undefined` on non-iOS platforms is a no-op.
 */
const IOS_ZERO_LINE_HEIGHT =
  Platform.OS === "ios" ? { lineHeight: 0 } : undefined;

/**
 * Props interface for TextInputField view
 */
interface CheckboxProps {
  control: Control<IForm>;
  /** The memoized field renderer, built by the Container. */
  renderInput: ReturnType<typeof makeNoteFieldRenderer>;
}

/**
 * Pure view component for text input field
 * @param root0 - Component props
 * @param root0.control - React Hook Form control object for form state management
 * @param root0.loading - Whether note submission is in progress
 * @param root0.errors - Form validation errors object
 * @param root0.isSmallScreen - Whether device has small screen size
 * @param root0.InputTextField - Text input component to use (regular or bottom sheet compatible)
 * @param root0.isDark - Whether dark theme is active
 */
/** What the field renderer needs, beyond the react-hook-form field itself. */
export interface NoteFieldRendererOptions {
  /** Whether note submission is in progress. */
  readonly loading: boolean;
  /** Form validation errors. */
  readonly errors: FieldErrors<IForm>;
  /** Whether the device has a small screen. */
  readonly isSmallScreen: boolean;
  /** Whether the dark theme is resolved. */
  readonly isDark: boolean;
  /**
   * Injected text-input component. The bare `TextInput` atom is used on the
   * non-bottom-sheet branches; `BottomSheetTextInput` is injected only inside a
   * BottomSheet provider. The atom omits the raw `style` prop in favour of
   * `UNSAFE_style`, so the prop type allows either and the iOS line-height
   * tweak below is passed as `UNSAFE_style`.
   */
  readonly InputTextField: React.ComponentType<
    Omit<TextInputProps, "style"> & {
      UNSAFE_style?: TextInputProps["style"];
    }
  >;
}

/**
 * Builds the `Controller` render prop for the note body field.
 *
 * A factory rather than a component so the caller can memoize it: `Controller`
 * re-renders the field whenever `render` changes identity, and the View that
 * used to build this held a `useCallback` to prevent exactly that — a hook a
 * View may not have (SE-8019). The Container memoizes the result instead.
 *
 * The markup stays in this file because it is presentation; moving a wall of
 * form JSX into a Container would trade one misplacement for another.
 * @param options - Everything the field needs beyond the form field itself.
 * @param options.loading - Whether note submission is in progress.
 * @param options.errors - Form validation errors.
 * @param options.isSmallScreen - Whether the device has a small screen.
 * @param options.isDark - Whether the dark theme is resolved.
 * @param options.InputTextField - The injected text-input component.
 * @returns The render prop.
 */
export const makeNoteFieldRenderer =
  ({
    loading,
    errors,
    isSmallScreen,
    isDark,
    InputTextField,
  }: NoteFieldRendererOptions) =>
  ({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "body">;
  }) => (
    <FormControl
      isDisabled={false}
      isInvalid={!loading && !!errors.body}
      isReadOnly={false}
      isRequired={false}
    >
      <Box className="h-24 flex-row overflow-hidden rounded-sm border-0 border-none bg-surface-raised">
        <InputTextField
          placeholder="Note"
          value={value ?? ""}
          keyboardType="default"
          inputMode="text"
          autoCorrect
          autoComplete="off"
          textContentType="none"
          autoCapitalize="none"
          returnKeyLabel="Next"
          enterKeyHint="next"
          onChangeText={onChange}
          onBlur={onBlur}
          multiline
          // design-system: runtime waiver — iOS multiline TextInput needs
          // lineHeight:0 to vertically center; lineHeight 0 has no closed
          // text-style token (the variants bundle non-zero leadings), so the
          // former iOS zero-leading arbitrary value is expressed as an
          // iOS-only single-brace style prop. No-op on other platforms.
          // eslint-disable-next-line no-restricted-syntax -- iOS-only zero lineHeight needed for multiline TextInput vertical alignment; no closed text-style token encodes lineHeight:0
          UNSAFE_style={IOS_ZERO_LINE_HEIGHT}
          className={`h-full flex-1 px-3 font-body ${!isSmallScreen ? "text-title" : "text-title-lg"} ${isDark ? "text-white placeholder:text-content-subtle" : "text-content-primary placeholder:text-content-muted"} web:cursor-text web:data-[disabled=true]:cursor-not-allowed`}
        />
      </Box>
      <FormControlError>
        <FormControlErrorIcon as={AlertCircleIcon} />
        <FormControlErrorText>{errors.body?.message}</FormControlErrorText>
      </FormControlError>
    </FormControl>
  );

/**
 * Renders the note body field.
 * @param root0 - Component props
 * @param root0.control - React Hook Form control object
 * @param root0.renderInput - The memoized field renderer
 * @returns The controlled field
 */
const InputFieldView = ({ control, renderInput }: CheckboxProps) => (
  <Controller name="body" control={control} render={renderInput} />
);

InputFieldView.displayName = "InputFieldView";

export default memo(InputFieldView);
